import React, { useState, useMemo } from "react";
import { useUser } from "@/app/context/UserContext";

import {
  Plus,
  Trash2,
  Pencil,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
} from "lucide-react";
import { validationProjectCuota } from "@/app/helpers/functions";
import { iQuotaGroups } from "@/app/models/interfaceQuotaConfiguration";

export function QuotaUserGroup() {
  const { userProject, setUserProject } = useUser();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // --- Estados para crear ---
  const [newGroupName, setNewGroupName] = useState("");
  const [newQuota, setNewQuota] = useState(0);
  const [newUsers, setNewUsers] = useState<string[]>([]);
  const [userInput, setUserInput] = useState("");
  const [addError, setAddError] = useState("");

  // --- Estados para editar ---
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editGroupName, setEditGroupName] = useState("");
  const [editQuota, setEditQuota] = useState(0);
  const [editUsers, setEditUsers] = useState<string[]>([]);
  const [editUserInput, setEditUserInput] = useState("");
  const [editError, setEditError] = useState("");

  const [viewAdd, setViewAdd] = useState(false);

  const itemsPerPage = 5;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const filtered = useMemo(
    () =>
      userProject.project.groups.filter(
        (g) =>
          g.name.toLowerCase().includes(search.toLowerCase()) ||
          g.users.some((u) => u.toLowerCase().includes(search.toLowerCase()))
      ),
    [search, userProject]
  );

  const paginated = filtered.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // --- Aux correos ---
  const addEmail = (
    value: string,
    current: string[],
    setter: (val: string[]) => void,
    resetInput: () => void,
    setError: (msg: string) => void
  ) => {
    const email = value.trim().toLowerCase();
    if (!email) return;
    if (!emailRegex.test(email)) {
      setError("Formato de correo inválido.");
      return;
    }
    if (current.includes(email)) {
      setError("El correo ya existe en este grupo.");
      return;
    }
    setter([...current, email]);
    setError("");
    resetInput();
  };

  const removeEmail = (
    email: string,
    current: string[],
    setter: (val: string[]) => void
  ) => {
    setter(current.filter((u) => u !== email));
  };

  // --- CRUD ---
  const handleAddGroup = () => {
    if (!newGroupName.trim()) { setAddError("El nombre del grupo es obligatorio."); return; }
    if (newQuota <= 0) { setAddError("La cuota debe ser mayor a 0."); return; }
    if (newUsers.length === 0) { setAddError("Debe agregar al menos un usuario."); return; } 
    const exists = userProject?.project?.groups?.some( (u) => u.name.toLowerCase() === newGroupName.toLowerCase());
    if (exists) { setAddError("El grupo ya fue agregado."); return; }
    const val = validationProjectCuota(userProject,newQuota); 
    if(val.status===false){ setAddError(val.message); return; }
    
    const updated: iQuotaGroups[] = [      
      { name: newGroupName, quota: newQuota, users: newUsers },
      ...userProject.project.groups,
    ];
    setUserProject({
      ...userProject,
      project: { ...userProject.project, groups: updated },
    });
    resetForm();
    setViewAdd(false);
  };

  const resetForm = () => {
    setNewGroupName("");
    setNewQuota(0);
    setNewUsers([]);
    setUserInput("");
    setAddError("");
  };

  const startEdit = (idx: number, group: iQuotaGroups) => {
    setEditingIndex(idx);
    setEditGroupName(group.name);
    setEditQuota(group.quota);
    setEditUsers(group.users);
    setEditUserInput("");
    setEditError("");
  };

  const saveEdit = (idx: number) => {
    if (!editGroupName.trim()) { setEditError("El nombre del grupo es obligatorio.");return; }
    if (editQuota <= 0) {setEditError("La cuota debe ser mayor a 0.");return;}
    if (editUsers.length === 0) {setEditError("Debe agregar al menos un usuario.");return;}
    const exists = userProject?.project?.groups?.some( (u,index) => u.name.trim().toLowerCase() === editGroupName.trim().toLowerCase() && index !== idx);
    if (exists) { setEditError("El grupo ya fue agregado."); return; }
    const updated = [...userProject.project.groups];
    const val = validationProjectCuota(userProject,updated[idx].quota??0,editQuota);    
    if(val.status===false){ setEditError(val.message); return; }

    //const updated = [...userProject.project.groups];
    updated[idx] = {
      name: editGroupName.trim(),
      quota: editQuota,
      users: editUsers,
    };
    setUserProject({
      ...userProject,
      project: { ...userProject.project, groups: updated },
    });
    setEditingIndex(null);
    setEditError("");
  };

  const deleteGroup = (idx: number) => {
    const updated = userProject.project.groups.filter(
      (_, i) => i !== idx
    );
    setUserProject({
      ...userProject,
      project: { ...userProject.project, groups: updated },
    });
  };

  return (
    <div className="p-4">
        {/* Formulario en una sola fila */}
        <div className="flex justify-end mb-3">
            <span className="font-semibold px-2 py-2">Agregar grupo de usuarios</span>
            <button
                onClick={() => setViewAdd(true)}
                className="flex items-center btn-inca-secondary justify-center px-2 py-2 w-10 h-10 rounded-full bg-gray-100 transition-colors border shadow-md  hover:bg-gray-200 animate-fade-in"
                title="Agregar grupo de usuarios"    
            >
                <Plus className="w-5 h-5" strokeWidth={1.5} />
            </button>                
        </div>
      {/* Buscador */}
      {((userProject?.project?.groups ?? []).length > itemsPerPage) && (
        <div className="relative flex-1 mb-4">
            <input
            type="text"
            placeholder="Buscar grupo o usuario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border rounded n-fmr-input-search border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
            />
            <Search className="absolute right-2.5 top-3.5 text-gray-400" size={18} />
        </div>
        )}

      {/* Encabezado */}
      <div className="grid grid-cols-4 font-medium text-gray-700 border-b pb-2 text-sm">
        <div className="col-span-2 font-semibold">Grupo</div>
        <div className="text-center font-semibold">Cuota</div>
        <div className="text-center font-semibold">Acción</div>
      </div>

      {/* Formulario de creación */}
      {viewAdd && (
        <div className="grid grid-cols-4 items-start border-b py-2 text-sm">
          {/* Columna Grupo */}
          <div className="col-span-2 flex flex-col gap-2">
            <input
              type="text"
              placeholder="Nombre del grupo"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className="w-full border n-fmr-input px-3 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
            />
            <div>
             
            <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Agregar correo"
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addEmail(
                        userInput,
                        newUsers,
                        setNewUsers,
                        () => setUserInput(""),
                        setAddError
                      );
                    }
                  }}
                  className="flex-1 border n-fmr-input px-3 py-2 border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-700"
                />
                <button
                  type="button"
                  onClick={() =>
                    addEmail(
                      userInput,
                      newUsers,
                      setNewUsers,
                      () => setUserInput(""),
                      setAddError
                    )
                  }
                  title="Agregar correo"
                  className="flex items-center justify-center w-9 h-9 rounded-full btn-inca-secondary text-white shadow-md" 
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

               

              <div className="flex flex-wrap gap-1 mt-2">
                {newUsers.map((email) => (
                  <span
                    key={email}
                    className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs flex items-center gap-1"
                  >
                    {email}
                    <X
                      size={12}
                      className="cursor-pointer hover:text-red-500"
                      onClick={() => removeEmail(email, newUsers, setNewUsers)} 
                    />
                  </span>
                ))}
              </div>
            </div> 
          </div>

          {/* Columna Cuota */}
            <div className="flex justify-center items-start relative ml-5">
                 <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"> $ </span>
                <input
                    type="number"
                    step="0.01" 
                    value={newQuota} 
                    min={1}
                    placeholder="Cuota"
                    onChange={(e) => setNewQuota(Number(e.target.value))}                  
                    className="w-full pl-6 border n-fmr-input px-3 py-2 border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-700 text-right"

                />
            </div>

      
          {/* Columna Acciones */}
          <div className="flex justify-center items-start gap-2">
            <button
              onClick={handleAddGroup}
              title="Guardar grupo"
              className="flex items-center justify-center w-8 h-8 text-white rounded-full bg-blue-600 hover:bg-blue-800"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                resetForm();
                setViewAdd(false);
              }}
              title="Cancelar"
              className="flex items-center btn-inca-secondary justify-center px-2 py-2 rounded-full transition-colors border shadow-md"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {addError && <p className="col-span-4 text-red-500 text-xs">{addError}</p>}
        </div>
      )}

      {/* Filas */}
      {paginated.map((g, idx) => (
        <div
          key={idx}
          className="grid grid-cols-4 items-start border-b py-2 text-sm pl-2"
        >
          {editingIndex === idx ? (
            <>
              {/* Columna Grupo */}
              <div className="col-span-2 flex flex-col gap-2">
                <input
                  type="text"
                  value={editGroupName}
                  onChange={(e) => setEditGroupName(e.target.value)}
                  className="w-full border n-fmr-input px-3 py-2 border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-700"
                />
                <div> 
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Agregar correo"
                      value={editUserInput}
                      onChange={(e) => setEditUserInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === ",") {
                          e.preventDefault();
                          addEmail(
                            editUserInput,
                            editUsers,
                            setEditUsers,
                            () => setEditUserInput(""),
                            setEditError
                          );
                        }
                      }}
                      className="flex-1 border n-fmr-input px-3 py-2 border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-700"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        addEmail(
                          editUserInput,
                          editUsers,
                          setEditUsers,
                          () => setEditUserInput(""),
                          setEditError
                        )
                      }
                      title="Agregar correo"
                      className="flex items-center justify-center w-9 h-9 rounded-full btn-inca-secondary text-white shadow-md"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex flex-wrap gap-1 mt-2">
                    {editUsers.map((email) => (
                      <span
                        key={email}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs flex items-center gap-1"
                      >
                        {email}
                        <X
                          size={12}
                          className="cursor-pointer hover:text-red-500"
                          onClick={() =>
                            removeEmail(email, editUsers, setEditUsers)
                          }
                          
                        />
                      </span>
                    ))}
                  </div>
                </div>
                
              </div>

              {/* Columna Cuota */}
              <div className="flex justify-center items-start relative ml-5">
                 <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500"> $ </span>
                <input
                    type="number"
                    step="0.01"
                    min={1}
                    value={editQuota}
                    onChange={(e) => setEditQuota(Number(e.target.value))}                  
                    className="w-full pl-6 border n-fmr-input px-3 py-2 border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-700 text-right"

                />
              </div>

              {/* Columna Acciones */}
              <div className="flex justify-center items-start gap-2">
                <button
                  onClick={() => saveEdit(idx)}
                  title="Guardar cambios"
                  className="flex items-center justify-center w-8 h-8 text-white rounded-full bg-blue-600 hover:bg-blue-800"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditingIndex(null)}
                  title="Cancelar edición"
                  className="flex items-center btn-inca-secondary justify-center px-2 py-2 rounded-full transition-colors border shadow-md"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              {editError && (
                  <p className="col-span-4 text-red-500 text-xs">{editError}</p>
                )}
            </>
          ) : (
            <>
              <div className="col-span-2">
                <p className="font-medium">{g.name}</p>
                <p className="text-xs text-gray-500">{g.users.join(", ")}</p>
              </div>
              <div className="text-center text-gray-600">{g.quota}</div>
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => startEdit(idx, g)}
                  title="Editar grupo"
                  className="flex items-center justify-center w-8 h-8 text-white rounded-full bg-blue-600 hover:bg-blue-800"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => deleteGroup(idx)}
                  title="Eliminar grupo"
                  className="flex items-center btn-inca-secondary justify-center px-2 py-2 rounded-full transition-colors border shadow-md"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      ))}

      {/* Paginación */}
      {filtered.length > itemsPerPage && (
        <div className="flex justify-end items-center mt-2 gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            title="Página anterior"
            className="p-1 border bg-slate-50 text-blue-500 rounded-full disabled:opacity-50 shadow-lg"
          >
            <ChevronLeft />
          </button>
          <span className="px-3 py-1 bg-blue-700 text-white rounded-full">
            {page} / {Math.ceil(filtered.length / itemsPerPage)}
          </span>
          <button
            onClick={() =>
              setPage((p) =>
                p < Math.ceil(filtered.length / itemsPerPage) ? p + 1 : p
              )
            }
            disabled={page * itemsPerPage >= filtered.length}
            title="Página siguiente"
            className="p-1 border bg-slate-50 text-blue-500 rounded-full disabled:opacity-50 shadow-lg"
          >
            <ChevronRight  />
          </button>
        </div>
      )}

      
    </div>
  );
}
